import React, {useEffect, useRef, useState, useCallback } from 'react';

import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl from 'maplibre-gl';
import { GeolocateControl, Map, NavigationControl } from 'react-map-gl';
import { useSelector, useDispatch, batch } from 'react-redux';
import { bbox } from '@turf/turf';

import { callGetFeatureJson } from 'handimap-react-lib/api/jsonFeatureApi';
import { selectFeature } from '../store/actions/selectedFeatureActions';
import { setContentType, setOpenSideBar } from '../store/actions/sideBarActions';
import { disableFlyTo } from '../store/actions/poiListingActions';
import { OriginDestMarkers } from './OriginDestMarkers';
import { PopupMap } from '../components/PopupMap';
import { PathsLayer } from '../layers/PathsLayer';
import { MapLayers } from '../layers/MapLayers';
import { useMarker } from '../utils/useMarker';
import { FeatureSelectedInfo } from './FeatureSelectedInfo';
import { callGetJson } from '../api/jsonConfApi';
import { setActiveGraphLayer, setBboxMap } from '../store/actions/mapActions';
import { getRadiusOfCenter } from '../utils/useGeoJson';
import { UserLocationMarker } from '../components/UserLocationMarker';

const navControlStyle= {
  right: 10,
  top: 10
};

const MapZone = (props) => {
  
  const {
    baseMaps, 
    extrasLayers, 
    baseGraph, 
    zoneFromUrl, 
    isMobile, 
    convertProfiltForApi, 
    isInteractive=true,
    hiddenButtons=false,
    status,
    allResults,
    dateSelected, 
    stylePath,
    withNavControl = true
  } = props;
  const mapRef = useRef();

  const geolocateControlRef = useRef();

  const activeBaseLayer = useSelector(state => state.map.activeBaseLayer);
  const layerConf = useSelector(state => state.layerConf);
  const layerList = layerConf.buttonsLayersState;
  
  const [mapIsLoaded, setMapIsLoaded] = useState(false);
  const [layerVisibleList, setLayerVisibleList] = useState([]);
  const [pathLayerList, setPathLayerList] = useState([]);
  const [listVisible, setListVisible] = useState([]);
  const [clickEnabled, setClickEnabled] = useState(true);

  const attributionControl = useRef(new maplibregl.AttributionControl());

  const [cursor, setCursor] = useState('auto');

  const baseGraphShown = useSelector(state => state.map.showBaseGraphe);
  const origin = [...useSelector(state => state.directions.origin.marker)];
  const destination = [...useSelector(state => state.directions.destination.marker)];

  const profil = useSelector(state => state.profils.profil);
  const condition = useSelector(state => state.profils.condition);
  const multimodal = useSelector(state => state.profils.multimodal);
  
  const selectedFeatureLayer = useSelector(state => state.selectedFeature);
  const {selected, layer_id, feature} = selectedFeatureLayer;

  const enablePoiFlyTo = useSelector(state => state.poiListingMenuReducer.flyTo);

  const showGeolocation = useSelector(state => state.map.showGeoLocation);
  const geoLocation = useSelector(state => state.map.geolocation);

  const obstaclesGeoJson = useSelector(state => state.obstacle.obstaclesFeatures);
  const showObstacles = useSelector(state => state.obstacle.showLayer);

  const baseLayers = baseMaps.layers.map(element => {
    return element.id;
  });
  const extrasLayersList = extrasLayers.layers.map(element => {
    return element.id;
  });

  const dispatch = useDispatch();
  const {setMarker} = useMarker();

  const handleFeatures = features => {
    if (features[0].source === 'selectedpath') {
      batch(() => {
        dispatch(selectFeature(features[0], features[0].layer.id));
        dispatch(setContentType('selectedPathDetails'));
      });
    } else if (features[0].source === 'obstacles-sources') {
      batch(() => {
        dispatch(selectFeature(features[0], features[0].layer.id));
        dispatch(setContentType('selectedObstacle'));
      });
    } else if (features[0].source === 'lignes_transports') { 
      batch(() => {
        dispatch(selectFeature(features, features[0].layer.id));
        dispatch(setContentType('selectedTransportDetails'));
      });
    } else if (features[0].source === baseGraph) {
      batch(() => {
        dispatch(selectFeature(features[0], features[0].layer.id));
        dispatch(setContentType('selectedGraphDetails'));
      });
    } else {
      batch(() => {
        dispatch(selectFeature(features[0], features[0].layer.id));
        dispatch(setContentType('selectedFeatureDetails'));
      });
    }
    if (isMobile) {
      setTimeout(() => {
        dispatch(setOpenSideBar(true));
      }, 500);
    }
  };

  const handleMouseUp = (e) => {
    const features = mapRef.current?.getMap().queryRenderedFeatures(e.point, {
      layers: listVisible
    });
    /**
     * batch()
     * ensure that multiple actions dispatched outside of React  
     * only result in a single render update
     */
    if (features.length > 0) {
      handleFeatures(features);
    } else {
      return;
    }
  };

  /**
   * load style visibility
   */
  const loadStyle = () => {
    for (let element of baseLayers) {
      if (element !== activeBaseLayer && !extrasLayersList.includes(element) && (!baseGraphShown || element !== baseGraph)) {
        mapRef.current?.getMap().setLayoutProperty(element, 'visibility', 'none');
      } else {
        mapRef.current?.getMap().setLayoutProperty(element, 'visibility', 'visible');
      }
    }
  };

  const onMouseEnter = useCallback(() => setCursor('pointer'), []);
  const onMouseLeave = useCallback(() => setCursor('auto'), []);
  const onEnableClick = useCallback(() => setClickEnabled(true), []);
  const onDisableClick = useCallback(() => setClickEnabled(false), []);

  const flyToMainCity = () => callGetJson('custom_info_city.json').then(
    result => {
      if (result && result !== null) {
        const longitude = result.longitude;
        const latitude = result.latitude;
        const zoom = result.zoom;
        const coordBboxString = result.bbox;
        const coordArray = coordBboxString.split(',').map(Number);
        mapRef.current?.flyTo({center: [longitude, latitude], zoom: zoom, duration: 500});
        dispatch(setBboxMap(coordArray));
      } else {
        return;
      }
    }
  );

  useEffect(() => {
    setLayerVisibleList([]);
    let newList = [];
    const layerGetFeatureList = layerConf.getFeatureInfoAttrs;
    for (const key in layerList) {
      if (layerList[key].active === true && key in layerGetFeatureList) {
        layerList[key].layersName.map((layer)=>{
          if (layer in layerGetFeatureList) {
            newList.push(layer);
          }
        });
      }
    }
    if (obstaclesGeoJson && showObstacles) {
      newList.push('obstacles-layer');
    }
    setLayerVisibleList(newList);
  }, [layerConf, obstaclesGeoJson, showObstacles]);

  useEffect(() => {
    loadStyle();
  }, [activeBaseLayer, baseGraph]);

  useEffect(() => {
    if (baseGraph !== undefined) {
      dispatch(setActiveGraphLayer(baseGraph));
    } else {
      return;
    }
  }, [baseGraph]);

  useEffect(() => {
    if (mapIsLoaded) {
      if (baseGraphShown) {
        mapRef.current?.getMap().setLayoutProperty(baseGraph, 'visibility', 'visible');
      } else {
        mapRef.current?.getMap().setLayoutProperty(baseGraph, 'visibility', 'none');
      }
    }
  }, [baseGraphShown]);

  useEffect(() => {
    if (baseGraphShown && baseGraph !== undefined) {
      setListVisible([baseGraph, ...layerVisibleList, ...pathLayerList]);
    } else {
      setListVisible([...layerVisibleList, ...pathLayerList]);
    }
  }, [baseGraph, baseGraphShown, layerVisibleList, pathLayerList]);

  useEffect(() => {
    if (mapRef.current !== undefined && mapRef.current !== null) {
      if (hiddenButtons) {
        if (mapRef.current.getMap().hasControl(attributionControl.current)) {
          mapRef.current.getMap().removeControl(attributionControl.current);
        }
      } else {
        if (! mapRef.current.getMap().hasControl(attributionControl.current)) {
          mapRef.current.getMap().addControl(attributionControl.current, isMobile ? 'bottom-left':'bottom-right');
        }
      }
    }
  }, [hiddenButtons]);

  useEffect(() => {
    mapRef.current?.once('load', () => setMapIsLoaded(true));
    if (mapRef.current !== undefined && mapRef.current !== null) {
      mapRef.current.setPadding({
        left: isMobile ? 0 :  380,
        bottom: isMobile ? 0 : 50
      });
      
      // Add attribution depending if responsive or not
      if (! mapRef.current.getMap().hasControl(attributionControl.current)) {
        mapRef.current?.getMap().addControl(attributionControl.current, isMobile ? 'bottom-left':'bottom-right');
      }
      // Disable rotation on phone
      mapRef.current?.getMap().touchZoomRotate.disableRotation();
      // init map position from conf
      if (zoneFromUrl !== null && zoneFromUrl !== undefined && zoneFromUrl !== '') {
        callGetJson('custom_zoom_zone_button.json').then(
          result => {
            const zoomByZone = Object.assign({}, ...result.map((x) => ({[x.zone]: x})));
            mapRef.current.flyTo({center: zoomByZone[zoneFromUrl].center, zoom: zoomByZone[zoneFromUrl].zoom, duration: 500});
          }).catch(
          () => {
            flyToMainCity();
          }
        );
      } else {
        flyToMainCity();
      }
    }
    mapRef.current?.once('idle', () => {
      if (mapRef.current !== undefined && mapRef.current !== null) {
        mapRef.current.resize();
      }
    });
    /**
       * Passer l'instance de map vers l'objet Window 
       * utilisé par les tests Cypress
       */
    window.mapInfo = mapRef.current;
  }, [mapRef.current]);



  useEffect(() => {
    if (mapIsLoaded) {
      if (mapRef.current !== undefined && mapRef.current !== null) {
        // Add attribution depending if responsive or not
        if (! mapRef.current.getMap().hasControl(attributionControl.current)) {
          mapRef.current?.getMap().addControl(attributionControl.current, isMobile ? 'bottom-left':'bottom-right');
        }
        // Disable rotation on phone
        mapRef.current?.getMap().touchZoomRotate.disableRotation();
        // init map position from conf
        if (zoneFromUrl !== null && zoneFromUrl !== undefined && zoneFromUrl !== '') {
          callGetJson('custom_zoom_zone_button.json').then(
            result => {
              const zoomByZone = Object.assign({}, ...result.map((x) => ({[x.zone]: x})));
              mapRef.current.flyTo({center: zoomByZone[zoneFromUrl].center, zoom: zoomByZone[zoneFromUrl].zoom, duration: 500});
            }).catch(
            () => {
              flyToMainCity();
            }
          );
        } else {
          if (status !== 'success') {
            flyToMainCity();
          } else {
            return;
          }
        }
      }
    }
  }, [mapIsLoaded]);

  useEffect(() => {
    // Ugly trick to make canvas map not focusable for accessibility
    document.addEventListener('DOMContentLoaded', function() {
      let canvas = document.getElementsByClassName('maplibregl-canvas mapboxgl-canvas').item(0);
      if (canvas !== undefined && canvas !== null) {
        canvas.removeAttribute('tabIndex');
      }
    });
    
  }, []);

  useEffect(() => {
    // zoom map on a feature, happens when a user click on button "see on the map" from the poi listing menu
    if (selected && enablePoiFlyTo) {
      dispatch(disableFlyTo());
      callGetFeatureJson(layer_id, feature.properties.fid).then(
        result => {
          const {geometry} = result;
          if (geometry.type.includes('Point')) {
            mapRef.current?.flyTo({center: geometry.coordinates, zoom: 17, duration: 500});
          }
          if (geometry.type.includes('LineString')) {
            const bounds = bbox(geometry);
            if (bounds !== undefined) {
              mapRef.current?.fitBounds(bounds, {
                padding: 10
              });
            } else {
              return;
            }
          }
        }
      ).catch(error => {
        console.log('error', error);
      });
    }
    
  }, [enablePoiFlyTo]);

  return (
    <Map 
      id='mymap'
      ref={mapRef}
      mapStyle={baseMaps}
      mapLib={maplibregl}
      width='100vw'
      height='100vh'
      /**
       * use function loadStyle for onLoad in case of 
       * change of mode responsive => unmount and remount of map 
       * => style is not loading correct when remount (load only osm-tiles)
       */
      onLoad={loadStyle}
      cursor={cursor}
      interactiveLayerIds={mapIsLoaded ? listVisible: []}
      attributionControl={false}
      dragRotate={false}
      onMouseUp={isInteractive && clickEnabled ? (e) => handleMouseUp(e): null}
      onMouseEnter={isInteractive && clickEnabled ? onMouseEnter: null}
      onMouseLeave={isInteractive && clickEnabled ? onMouseLeave: null}
      styleDiffing
    >
      { !hiddenButtons && withNavControl && 
        <>
          <GeolocateControl 
            position={isMobile ? 'top-right' : 'top-left'} 
            ref={geolocateControlRef}
            trackUserLocation={true}
            showUserHeading={true}
            positionOptions={{ enableHighAccuracy: true }}
          />
          <NavigationControl position={isMobile ? 'top-right' : 'top-left'} showCompass={false} style={navControlStyle}/>
        </>
      }
      {
        showGeolocation && geoLocation.length > 0 && (
          <UserLocationMarker
            longitude={geoLocation[0]}
            latitude={geoLocation[1]}
            data={getRadiusOfCenter(geoLocation)}
          />
        )
      }
      <OriginDestMarkers 
        disableClick={onDisableClick}
        enableClick={onEnableClick}
        setMarker={(lngLat, markerCase) => setMarker(lngLat, markerCase)} 
      />
      <PopupMap setMarker={(lngLat, markerCase) => setMarker(lngLat, markerCase)}/>
      <PathsLayer 
        origin={origin} 
        destination={destination} 
        multimodal={multimodal} 
        profil={profil} 
        condition={condition}
        dateSelected={dateSelected}
        pathLayerList={pathLayerList}
        setPathLayerList={setPathLayerList} 
        convertProfiltForApi={convertProfiltForApi}
        status={status}
        allResults={allResults}
        stylePath={stylePath}
      />
      {selected && layer_id !== '' && isInteractive && (
        <FeatureSelectedInfo layer_id={layer_id} feature={feature} />
      )}
      <MapLayers/>
      <div>
        {props.children}
      </div>
    </Map>
  );
};

export default MapZone;